/**
 * Qué se está reconectando mientras el usuario está en la plataforma (tanda «Reconexión de pauta»,
 * contrato §2.1): la cuenta que se arregla desde su «Reconectar» (`adAccountId`) o la conexión que se
 * renueva desde Plataformas (`credentialId`), con el canal.
 *
 * El callback del canal que vuelve la consume UNA vez, salga bien o mal, y sólo la usa si es de su
 * canal. Antes la cuenta iba suelta en `pending_reconnect_ad_account_id`, sin canal ni vencimiento: Meta
 * no la leía nunca, y el OAuth siguiente de otro canal se la llevaba y el backend rechazaba el reassign
 * por canal distinto.
 *
 * Vive en `localStorage` como las demás notas del alta (el OAuth vuelve en la misma pestaña) y vence a
 * los 15 minutos: un «Reconectar» abandonado no puede colarse en una conexión nueva más tarde.
 * LinkedIn tiene su propia nota (`pendingLinkedinReconnect`), más vieja y con el mismo criterio.
 */

const KEY = 'pending_reconnect';
// Las claves de antes de la tanda: se borran siempre, para que un bundle viejo abierto en otra
// pestaña no deje una cuenta suelta que el callback nuevo ignore pero nadie limpie.
const LEGACY_KEYS = ['pending_reconnect_ad_account_id', 'pending_reconnect_channel_name'];
export const RECONNECT_NOTE_MAX_AGE_MS = 15 * 60 * 1000;

function storage() {
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
}

function asId(value) {
    return value === null || value === undefined || value === '' ? null : String(value);
}

/** Anota la reconexión que arranca. Pisa cualquier nota anterior. */
export function rememberReconnect(channel, {adAccountId = null, credentialId = null} = {}) {
    const store = storage();
    if (!store) return;
    try {
        LEGACY_KEYS.forEach((key) => store.removeItem(key));
        store.setItem(KEY, JSON.stringify({
            channel,
            adAccountId: asId(adAccountId),
            credentialId: asId(credentialId),
            at: Date.now(),
        }));
    } catch (error) {
        // Sin localStorage la conexión sale igual, sin decirle al backend qué se arreglaba.
    }
}

/** Borra la nota (una conexión nueva no hereda nada de un «Reconectar» abandonado). */
export function forgetReconnect() {
    const store = storage();
    if (!store) return;
    try {
        store.removeItem(KEY);
        LEGACY_KEYS.forEach((key) => store.removeItem(key));
    } catch (error) {
        // Nada que borrar.
    }
}

function readNote(channel) {
    const store = storage();
    let note = null;
    try {
        note = store ? JSON.parse(store.getItem(KEY)) : null;
    } catch (error) {
        note = null;
    }
    if (!note || note.channel !== channel || typeof note.at !== 'number'
        || Date.now() - note.at > RECONNECT_NOTE_MAX_AGE_MS) {
        return null;
    }
    const adAccountId = asId(note.adAccountId);
    const credentialId = asId(note.credentialId);
    if (!adAccountId && !credentialId) return null;
    return {adAccountId, credentialId};
}

/**
 * La nota `{adAccountId, credentialId}` si es de `channel` y sigue vigente; si no, `null`. La borra
 * siempre: usada, vencida o de otro canal, no sobrevive a esta lectura.
 */
export function consumeReconnect(channel) {
    const note = readNote(channel);
    forgetReconnect();
    return note;
}

/**
 * La misma nota, SIN borrarla. La usa Meta: su modal queda abierto si el popup se cancela o el pedido
 * falla, y el reintento desde ahí tiene que seguir llevando la cuenta (C-rp-fe-01). La borra el éxito,
 * o cerrar el modal.
 */
export function peekReconnect(channel) {
    return readNote(channel);
}
